// client/src/features/portal/search.ts — the portal's URL state · PORTAL-002 phase 2 (04 §9.2.3).
//
// SEVEN SCHEMAS, AND ONE OF THEM IS ON THE LAYOUT ROUTE. `portalSearchSchema` is validated by
// `/portal` itself, so `contract` is part of the full search of every `/portal/*` screen and a
// child route neither redeclares it nor can lose it — which is what makes „which of my
// contracts am I looking at" one fact rather than six screens' component state. See D-1180.
//
// The other six belong to one screen each and are declared there, because nothing else reads
// them: the rent schedule's status filter and page, the notification feed's page and its unread
// filter, whether the incident report form is open, which page of SCR-PORT-005's document list
// is shown, SCR-PORT-003's page of the meter list (D-1562) and the billing month its bills
// section is showing (D-2197), and which section of the settings page is in view (D-3306).
//
// ── the boolean spelling ───────────────────────────────────────────────────────────────────
// `zUrlBoolean`, not `z.boolean()`, for D-1197's reason: TanStack Router parses search values
// with `JSON.parse`, so a SHARED `?unreadOnly=true` arrives as the boolean `true` while the
// same filter set by clicking the control arrives as the string `'true'` — and a schema that
// admits one of the two renders a Zod dump instead of the screen. `contract` needs none of
// that: a GUID is not valid JSON, so `JSON.parse` throws and the router hands back the raw
// string, which is what `z.string()` wants.
//
// ── the status chips reuse the landlord's tuple ────────────────────────────────────────────
// `REPAIR_TICKET_STATUSES` is `features/repairs/enums.ts`'s, imported rather than copied, for
// the reason `photo.ts` re-exports the vault's two numbers: a second listing of the same wire
// union is the drift that stops offering a member the day the contract gains one. The tuple is
// `as const satisfies readonly RepairTicketStatus[]`, so a member the API drops is a compile
// error here and not a chip that filters to nothing.

import { z } from 'zod';

import type { PortalDocumentSource, RentScheduleStatus } from '@/client';
import { REPAIR_TICKET_STATUSES, REPAIR_URGENCIES } from '@/features/repairs/enums';
import { urlBoolean, zUrlBoolean } from '@/lib/url-state';

/**
 * `/portal`'s own search — the contract in view, and nothing else.
 *
 * Optional, and absent is the ordinary case: a tenant with one contract never sees the
 * selector (§6.6: „when the tenant has several") and never has this in their URL.
 * `contractInView` resolves the absence, and resolves an id that is not the caller's the same
 * way — see `scope.ts`.
 */
export const portalSearchSchema = z.object({
  contract: z.string().trim().optional(),
});

export type PortalSearch = z.infer<typeof portalSearchSchema>;

/**
 * The statuses SCR-PORT-002's filter offers, in the order a tenant reads them: what is not
 * settled first, what is settled after it.
 *
 * The whole `RentScheduleStatus` catalogue and not the statuses the payload happens to carry —
 * a filter whose options appear and disappear with the data is one a tenant cannot get back out
 * of, and `satisfies` makes a member added to the wire enum a compile error here rather than an
 * option nobody can select.
 */
export const RENT_STATUS_FILTERS = [
  'PENDING',
  'OVERDUE',
  'PARTIALLY_PAID',
  'PAID',
  'CANCELLED',
] as const satisfies readonly RentScheduleStatus[];

/**
 * SCR-PORT-002: which statuses are in view, and which page of them.
 *
 * In the URL rather than in component state for the reason the feed's page is (§9.2.3): a
 * tenant who filters to „Întârziată" and opens a scadență can come Back to the view they were
 * reading, and the same link sent to the landlord opens on the same rows.
 *
 * Both are the SCREEN's, not the request's: `GET /portal/contracts/{id}/schedule` answers a
 * whole contract's periods in one payload — a lease is a few dozen rows, not a page — so the
 * filter and the pager are applied to what the one request already returned. No status ever
 * travels to the server, and switching pages issues nothing.
 */
export const portalRentSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  status: z.enum(RENT_STATUS_FILTERS).optional(),
});

export type PortalRentSearch = z.infer<typeof portalRentSearchSchema>;

/**
 * The bell's screen: one page of the feed, optionally only the unread — and `settings`, which the
 * screen never shows.
 *
 * `settings=1` is the link every renter's notification mail carries under „Preferințe de
 * notificare” (D-3014). It opened „Notificări pe e-mail” over the feed until the switches became a
 * section of the settings page (D-3306); old mails keep the link, so the route sends it on to that
 * section, replacing the entry (D-3309). Coerced, because the router hands back the number 1 for a
 * shared link and a string for one typed by hand; anything else is the feed.
 */
export const portalNotificationsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  unreadOnly: zUrlBoolean.optional(),
  settings: z.coerce.number().int().optional().catch(undefined),
});

export type PortalNotificationsSearch = z.infer<typeof portalNotificationsSearchSchema>;

/**
 * The sections of the portal's settings page, in the page's order (D-3306). Each is a
 * `section-card` with the id `settingsSectionId` gives it, and each can be linked to as
 * `/portal/settings?section=<key>`.
 */
export const PORTAL_SETTINGS_SECTIONS = [
  'profile',
  'preferences',
  'password',
  'sessions',
  'owner',
  'deletion',
] as const;

/**
 * Every value `?section=` admits: the six sections, and `address` — not a section but the sign-in
 * address inside „Profil”, which the parameter also OPENS (the change-of-address form), so
 * „Deveniți proprietar”'s „Adăugați o adresă” can land a person on the form it is asking for.
 */
export const PORTAL_SETTINGS_TARGETS = [...PORTAL_SETTINGS_SECTIONS, 'address'] as const;

export type PortalSettingsTarget = (typeof PORTAL_SETTINGS_TARGETS)[number];

/** The element id a target is scrolled to — prefixed, so it cannot meet an id the shell uses. */
export function settingsSectionId(target: PortalSettingsTarget): string {
  return `settings-${target}`;
}

/**
 * The settings page (D-3306): which section a link points at.
 *
 * A search parameter and not a `#hash`, for the reason every other piece of portal URL state is
 * one (§9.2.3): it is validated, typed into every `Link` that sets it, and carried beside the
 * layout's `contract` by the same spread. The page scrolls the section into view whenever the
 * value changes. `.catch(undefined)`, so a hand-edited `?section=nope` is the top of the page.
 */
export const portalSettingsSearchSchema = z.object({
  section: z.enum(PORTAL_SETTINGS_TARGETS).optional().catch(undefined),
});

export type PortalSettingsSearch = z.infer<typeof portalSettingsSearchSchema>;

/**
 * SCR-PORT-004: whether the report form is open, and which slice of the history is on screen.
 *
 * `report` is in the URL rather than in component state so that „Raportează un incident" is a
 * link a landlord can send, Back closes the form instead of leaving the portal, and a reload
 * does not discard a half-typed description by navigating away from it.
 *
 * The other three are in the URL for the reason every list screen in this product puts them
 * there (§9.2.3): a filtered view is a thing a tenant shares with the landlord („uite ce am
 * raportat și nu s-a rezolvat"), Back is what undoes a filter, and a reload lands on the page
 * that was being read. They are also exactly what the REQUEST carries — the narrowing is the
 * server's — so the URL and the query string say the same thing.
 *
 * `status` and `urgency` are validated against the wire unions rather than typed as free strings:
 * a select can only set a member, and a hand-edited `?status=BROKEN` would otherwise be sent to
 * the API and come back a 400 the tenant did not cause. An unknown value is dropped to „all".
 */
export const portalIncidentsSearchSchema = z.object({
  report: zUrlBoolean.optional(),
  page: z.coerce.number().int().min(1).optional(),
  q: z.string().trim().max(200).optional(),
  status: z.enum(REPAIR_TICKET_STATUSES).optional().catch(undefined),
  /** How urgent — the server's filter, where „Normal" includes the older incidents (D-2530). */
  urgency: z.enum(REPAIR_URGENCIES).optional().catch(undefined),
  activeOnly: zUrlBoolean.optional(),
});

export type PortalIncidentsSearch = z.infer<typeof portalIncidentsSearchSchema>;

/**
 * Whether anything is narrowing the list — what the empty state needs in order to say WHY it is
 * empty: „nu ai raportat încă nimic" and „niciun incident nu se potrivește" are different
 * sentences, and offering „raportează un incident" under the second one is the wrong offer.
 */
export function hasIncidentFilters(search: PortalIncidentsSearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.status !== undefined ||
    search.urgency !== undefined ||
    urlBoolean(search.activeOnly) === true
  );
}

/**
 * The two sides of SCR-PORT-005's archive a renter can ask for (D-2161): the landlord's papers,
 * or their own submissions. Two of the wire enum's three members on purpose — `LINK` is what a
 * ROW says about itself (a submission made through a no-account link), not a side of the room;
 * the server folds those rows into `TENANT` and refuses `LINK` as a filter with a 400. `satisfies`
 * makes a member the contract drops a compile error here rather than a chip that filters to
 * nothing.
 */
export const DOCUMENT_SOURCE_FILTERS = [
  'LANDLORD',
  'TENANT',
] as const satisfies readonly PortalDocumentSource[];

export type DocumentSourceFilter = (typeof DOCUMENT_SOURCE_FILTERS)[number];

/**
 * SCR-PORT-005: which side of „Documente" is shown, and which page of it.
 *
 * In the URL rather than in component state for the reason §9.2.3 gives and the incident form's
 * `report` follows (D-1190): a reload of page 3 comes back to page 3 rather than to the top of
 * the archive, and the page a tenant is reading is a link they can send. The segment travels
 * the same way (D-2161): absent is the landlord's side, which is the default and the view every
 * renter opens on, and `source=TENANT` is their own. Changing the segment resets the page.
 *
 * `source` is validated against the two-member tuple rather than typed as a free string, for
 * the incident chips' reason: a hand-edited `?source=LINK` would otherwise be sent to the API and
 * come back a 400 the renter did not cause. An unknown value is dropped to the default.
 *
 * There is no `document` param, because there is no viewer to address: the row's only action is
 * the download, which leaves no screen behind it (D-1513).
 */
export const portalDocumentsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  source: z.enum(DOCUMENT_SOURCE_FILTERS).optional().catch(undefined),
});

export type PortalDocumentsSearch = z.infer<typeof portalDocumentsSearchSchema>;

/**
 * SCR-PORT-003: which page of the meter list is shown, and which billing month the bills
 * section below it is showing.
 *
 * `page` is in the URL for the reason the rent schedule's page is (§9.2.3, D-1514): a reload
 * comes back to the meters that were being read rather than to the top of the list, and the
 * §6.6 `contract` on the layout route travels beside it untouched.
 *
 * `year` and `month` arrived with the bills (D-2197) and are the §9.5 litmus applied to a second
 * piece of this screen's state: the month a tenant is looking at is a thing they reload, share
 * with their landlord and come Back from. They drive the BILLS alone — the meter cards keep
 * their own page and their own window, which is why this schema has three keys rather than one
 * period for the whole screen.
 *
 * BOTH ARE OPTIONAL AND THEIR ABSENCE IS THE ORDINARY CASE. No `year`/`month` means „the current
 * month", and it means it on the SERVER: `GET /portal/contracts/{id}/utility-bills` answers for
 * „luna curentă (Europe/Bucharest)" when the query carries neither, and Bucharest's today is a
 * behaviour this repository keeps behind `IClock` rather than deriving from a browser's clock
 * (D-1127). The screen therefore sends nothing until a tenant names a month, and reads the month
 * it is showing back off the response.
 *
 * `.catch(undefined)` on both, for the reason `portalIncidentsSearchSchema`'s `status` carries
 * it: a hand-edited `?month=13` must fall back to the current month rather than render a Zod
 * dump where the screen was.
 */
export const portalMetersSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional().catch(undefined),
  month: z.coerce.number().int().min(1).max(12).optional().catch(undefined),
});

export type PortalMetersSearch = z.infer<typeof portalMetersSearchSchema>;

/** 02 §7.4's default page size, spelled here so a shared URL and the request agree. */
export const NOTIFICATIONS_PAGE_SIZE = 25;

/**
 * Six scadențe to a page.
 *
 * Small on purpose, and smaller than the feed's 25: this is a phone-first screen (§11.5) where
 * every row is a stacked card, and six of them is roughly what a tenant can see without the
 * table turning into a scroll. A year's lease is then two pages, which is also how a tenant
 * thinks about it.
 */
export const RENT_PERIODS_PAGE_SIZE = 6;

/**
 * SCR-PORT-004's page size.
 *
 * Five, not §7.4's twenty-five: an incident renders as a CARD with a title, a two-line
 * description, a line of context and a „Vezi detalii" button (§8.16's LIST on a phone is not a
 * table), so five of them is already more than one screenful on the smallest viewport §11.1
 * supports. The number travels in the request, so the SERVER pages to it and the pin above
 * holds across the page boundary rather than inside whatever arrived.
 */
export const INCIDENTS_PAGE_SIZE = 5;

/**
 * SCR-PORT-005's page size, and deliberately not §7.4's 25.
 *
 * The portal's content column is `max-w-2xl` and the rows are cards (§11.3), so six of them is
 * about one screenful on a phone: the pager stays where a thumb finds it without scrolling the
 * list to its end. The server's own default stays 25 — this is the client's request, not the
 * contract's bound.
 */
export const DOCUMENTS_PAGE_SIZE = 6;

/**
 * Four contoare to a page (D-1562).
 *
 * The smallest page size in the portal, and the rows are the reason: a meter renders as a card
 * carrying its name, its index, the provider line, the state sentence and TWO buttons, so four
 * of them already fill the 360 px viewport §11.1 supports. §11.2's flow P4 — „camera-first,
 * reading entry ≤ 30 s” — is what the number is chosen against: the meter a tenant is standing
 * in front of should be reachable without scrolling past the ones they are not.
 *
 * Applied HERE and not in the request: `GET /portal/contracts/{id}/meters` answers a bare array
 * (`usePortalMeters`), so turning a page issues nothing and every meter's window stays the one
 * the single response carried.
 */
export const METERS_PAGE_SIZE = 4;
